import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input, Renderer2, TrackByFunction, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ListComponentGeneric } from '../../../ui/generics';
import { NotificationService } from '../../../core/services';
import { IArticle, ITableResults } from '../../../core/interfaces';
import { ArticlesService } from '../core/ArticlesService';
import { ArticlesServiceErrorResolverService } from '../core/error-resolvers/articles-service-error-resolver.service';
import { ToolsService } from '../../../core/services/ToolsService';
import { AddEditArticleModalComponent } from '../../../shared/modals/add-edit-article-modal/add-edit-article-modal.component';
import { forkJoin } from 'rxjs';
import { OwnProductionService } from '../../own-production/core/OwnProductionService';
import { cloneDeep } from 'lodash';
import { FilterOperator } from '../../../core/types';

@Component({
    selector: 'app-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ListComponent extends ListComponentGeneric<IArticle> implements OnInit {

  @ViewChild('modal') modal: AddEditArticleModalComponent;

  @Input() articleId: number | null = null;

  private _originalData: ITableResults<IArticle>;

  constructor(
    protected _service: ArticlesService,
    protected _cdr: ChangeDetectorRef,
    protected _notify: NotificationService,
    protected renderer: Renderer2,
    protected _errorResolver: ArticlesServiceErrorResolverService,
    private readonly _toolsService: ToolsService,
    private readonly _ownProductionService: OwnProductionService,
    private readonly _router: Router,
  ) {
    super(_service, _cdr, _notify, renderer, _errorResolver);
  }

  searchColumns = [
    'name',
    'orderNumber',
    'id',
    'variantOrderNumbers'
  ];

  protected readonly exportedTableVisibleName = 'Artikelliste';
  public readonly trackByArticle: TrackByFunction<IArticle> = (_index, article) => article.id;

  public clearCache(): void {
    this._toolsService.clearCache().subscribe(() => {
      const { pageIndex, pageSize, filter, sortField, sortOrder, search } = this.lastLoadParams;
      this.load(pageIndex, pageSize, sortField, sortOrder, filter, search);
    }, this.parseError.bind(this));
  }

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    if (!this.articleId) {
      return;
    }

    this._service.getById(String(this.articleId)).subscribe({
      next: article => this.openModal(article),
      error: () => this._router.navigateByUrl('/articles'),
    });
  }

  public override onListDataAvailable(): void {
    super.onListDataAvailable();
    this._originalData = cloneDeep(this.data);
  }

  public openModal(article: IArticle, timeout = 0): void {
    setTimeout(() => {
      this.modal.showModal(article);
      this._cdr.detectChanges();
    }, timeout);
  }

  public isNonPackableChange(article: IArticle): void {
    if (article.isNonPackable) {
      article.isBagable = false;
      article.isOwnProduction = false;
    } else {
      article.needsGermanLabel = false;
    }

    this.updateArticleFlags(article);
  }

  public needsGermanLabelChange(article: IArticle, value: boolean): void {
    article.needsGermanLabel = value;
    this.updateArticleFlags(article);
  }

  public disableIsNonPackable(article: IArticle): void {
    if (article.isBagable || article.isOwnProduction) {
      article.isNonPackable = false;
      article.needsGermanLabel = false;
    }

    this.updateArticleFlags(article);
  }

  public callService(
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filters: Array<{ key: string; value: string[]; operator?: FilterOperator; }>,
    search: Array<{ key: string; value: string[]; operator?: FilterOperator; }>,
    additionalFilters: Array<{ key: string; value: string[]; operator?: FilterOperator; }>,
  ): void {
    if (this.currentSearch && this.currentSearch.length === 13 && !isNaN(+this.currentSearch)) {
      search = [];
      additionalFilters.push({ key: 'ean', value: [this.currentSearch] });
    }

    super.callService(pageIndex, pageSize, sortField, sortOrder, filters, search, additionalFilters);
  }

  public updateArticleFlags(article: IArticle): void {
    const { isOwnProduction: oldIsOwnProduction } = this._originalData.results.find(x => x.id === article.id);
    const { isOwnProduction: newIsOwnProduction } = article;
    const updateArticleApiCall = this._service.update(
      {
        isBagable: article.isBagable,
        isNonPackable: article.isNonPackable,
        needsGermanLabel: article.needsGermanLabel,
        isOwnProduction: article.isOwnProduction,
        noWeightForProduction: article.noWeightForProduction,
      } as IArticle,
      article.id
    );
    const updateFormulaIngredientsApiCall = this._ownProductionService.updateFormulaIngredientsByArticleId(
      article.id,
      {
        articleFormulaIngredients: []
      }
    );
    const apiCalls = [updateArticleApiCall, updateFormulaIngredientsApiCall];

    if (oldIsOwnProduction && newIsOwnProduction) {
      forkJoin(apiCalls).subscribe({
        next: this.pushSaveFormSuccess.bind(this),
        error: this.pushSaveFormError.bind(this)
      });
    } else if (oldIsOwnProduction && !newIsOwnProduction) {
      updateArticleApiCall.subscribe({
        next: this.pushSaveFormSuccess.bind(this),
        error: this.pushSaveFormError.bind(this)
      });
    } else if (!oldIsOwnProduction && newIsOwnProduction) {
      updateArticleApiCall.subscribe({
        next: () => {
          updateFormulaIngredientsApiCall.subscribe({
            next: this.pushSaveFormSuccess.bind(this),
            error: this.pushSaveFormError.bind(this)
          });
        },
        error: this.pushSaveFormError.bind(this)
      });
    } else {
      updateArticleApiCall.subscribe({
        next: this.pushSaveFormSuccess.bind(this),
        error: this.pushSaveFormError.bind(this)
      });
    }
  }

  private pushSaveFormError(error: any): void {
    this.parseError(error);
    this.onRefreshList(false);
  }

  private pushSaveFormSuccess(): void {
    this._notifyService.pushSuccess('Erfolgreich', `Element wurde korrekt aktualisiert.`);
    this.onRefreshList(false);
  }
}
